import { ReactNode } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { Icon, type IconProps } from '~/global/components/icon/Icon';
import { useRightSidebarDrawer } from '~/layout/components/right-sidebar/RightSidebarDrawerContext';
import { RightSidebarCloseButton } from '~/layout/components/right-sidebar/RightSidebarCloseButton';

export interface RightSidebarPanelHeaderProps {
  /** lucide icon name (PascalCase) shown left of the title. */
  icon: IconProps['name'];
  /** Panel title. */
  title: string;
  /** Optional right-aligned actions (e.g. new-chat button). */
  actions?: ReactNode;
}

/**
 * Shared header for right-sidebar panels (chat list, chat, favourites). One
 * 72px row: an icon + title on the left and optional actions on the right.
 * Exposed as {@link RightSidebar.PanelHeader} so every rail panel reads the same.
 *
 * The close (`X`) button is not a prop: when the panel is hosted by the mobile
 * {@link RightSidebar.Drawer} in single-tab mode it is injected here from the
 * drawer scope. With a rail strip (2+ tabs) the strip owns the `X`, and on
 * desktop there is no drawer scope at all — so the header stays clean.
 */
export const RightSidebarPanelHeader = ({
  icon,
  title,
  actions,
}: RightSidebarPanelHeaderProps) => {
  const drawer = useRightSidebarDrawer();
  const showClose = drawer?.headerShowsClose ?? false;

  return (
    <View className="flex-row items-center justify-between px-5 py-4">
      <View className="flex-row items-center gap-2.5">
        <Icon name={icon} size={18} className="text-text-primary" />
        <Text className="text-base font-sora-semibold text-text-primary">
          {title}
        </Text>
      </View>
      {actions || showClose ? (
        <View className="flex-row items-center gap-1">
          {actions}
          {showClose && drawer && (
            <RightSidebarCloseButton onPress={drawer.close} />
          )}
        </View>
      ) : null}
    </View>
  );
};
